/**
 * Date Field Type
 *
 * Provides configuration and renderers for date fields:
 * - Read-only column renderer
 * - Editable column renderer
 * - Form field renderer
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { format } from 'date-fns';
import { t } from '@lingui/macro';
import { DatePicker } from 'components';
import { DatePickerCell, TableTooltip } from '../../components';
import { Text } from 'components/text';
import { CellContainer, formInputSx } from '../../styles';
import { Box } from '@mui/material';
import type {
  FieldTypeConfig,
  FormFieldProps,
  ColumnRenderer,
  RenderOptions,
  CustomColumn,
} from '../../types';
import { validateFieldValue } from '../../validation';
import { FieldError, shouldShowFieldError } from './fieldError';
import { M } from './columnWidths';
import { ClearFieldOption } from '../../components/ClearFieldOption';
import { ClearedFieldIndicator } from '../../components/ClearedFieldIndicator';
import { getBulkEditClearState } from '../../hooks/useBulkEditClear';

// ============================================================================
// Editable Column Renderer
// ============================================================================

// Import shared cell value utility
import { getCellDateValue } from './cellValue';

// Default date formatter
const defaultDateFormatter = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'dd-MMM-yyyy');
  } catch {
    return t`Invalid date`;
  }
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Date read-only renderer - displays formatted date in a cell container
 * Uses getValue() to support nested accessors
 */
export const dateReadOnlyRenderer = <T extends { id: number | string }>(
  _col: CustomColumn<T>,
  options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const dateFormatter = options.dateFormatter ?? defaultDateFormatter;

  // Helper to get tooltip text for a date value
  const getTooltipText = (value: unknown): string => {
    if (!value) return t` `;
    try {
      return format(new Date(value as string), 'dd-MMM-yyyy');
    } catch {
      return String(value);
    }
  };

  return {
    cell: (cellContext: CellContext<T, unknown>) => {
      const value = cellContext.getValue();
      const tooltipText = getTooltipText(value);

      return (
        <TableTooltip title={tooltipText}>
          <CellContainer>
            <Text variant="bodyXs">{dateFormatter(value as string | null)}</Text>
          </CellContainer>
        </TableTooltip>
      );
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const value = cell.getValue();
      return getTooltipText(value);
    },
  };
};

// ============================================================================
// Form Field Renderer
// ============================================================================

/**
 * Render a date picker field for forms
 */
export const renderDateField = ({
  col,
  value,
  valueForValidation,
  onChange,
  disabled,
  bulkEditContext,
  validationContext,
}: FormFieldProps): React.ReactNode => {
  const emptyDisplayNode = (col as { emptyDisplayNode?: React.ReactNode }).emptyDisplayNode;
  const valueEmpty = value == null || value === '';
  const showEmptyChip = valueEmpty && emptyDisplayNode;

  // Use Zod validation to get error message (valueForValidation for bulk-edit "keep current")
  const valueToValidate = valueForValidation !== undefined ? valueForValidation : value;
  const validation = validateFieldValue({ ...col, type: 'date' }, valueToValidate);
  const errorMessage = !validation.success ? validation.error : undefined;
  const showError = shouldShowFieldError(col.key, validationContext);

  const { isBulkEdit, isCleared, showClearButton, handleClear, handleUndoClear } =
    getBulkEditClearState({
      value,
      valueForValidation,
      valueEmpty,
      colKey: col.key,
      required: (col as { required?: boolean }).required,
      onChange,
      bulkEditContext,
    });

  const handleChange = (stringDate: string | null) => {
    onChange(col.key, stringDate);
  };

  const handleBlur = (stringDate: string | null) => {
    onChange(col.key, stringDate);
    validationContext?.onFieldTouched?.(col.key);
  };

  if (isBulkEdit && isCleared) {
    return (
      <Box key={col.key}>
        <ClearedFieldIndicator fieldName={col.label} onClick={handleUndoClear} />
      </Box>
    );
  }

  return (
    <Box key={col.key}>
      <DatePicker
        id={`${col.key}-datepicker`}
        label={col.label}
        placeholder={showEmptyChip ? '' : col.placeholder}
        disabled={disabled}
        inputVariant="outlined"
        labelSeparate
        initialDate={(value as string) || ''}
        onChange={(_, _date, reason, stringDate) => {
          if (reason === 'clear' && bulkEditContext?.isBulkEdit) {
            bulkEditContext.onKeepCurrent();
            return;
          }
          if (reason === 'input' || reason === 'createOption') return;
          handleChange(stringDate);
        }}
        onBlur={(_date, stringDate) => handleBlur(stringDate)}
        sx={formInputSx}
        InputProps={showEmptyChip ? { startAdornment: emptyDisplayNode } : undefined}
        formatErrorHelperText=""
        calendarFooter={
          showClearButton ? <ClearFieldOption handleClick={handleClear} /> : undefined
        }
      />
      <FieldError error={errorMessage} show={showError} />
    </Box>
  );
};

/**
 * Date editable renderer - unified pattern
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 */
export const dateEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const editableProp = col.editable;
  const dateFormatter = options.dateFormatter ?? defaultDateFormatter;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellDateValue(context, col.id);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      if (!isRowEditable) {
        return (
          <CellContainer>
            <Text variant="bodyXs">{dateFormatter(value)}</Text>
          </CellContainer>
        );
      }

      return (
        <DatePickerCell
          context={context}
          value={value}
          onUpdate={newDate => {
            if (col.onUpdate) {
              return col.onUpdate(context.row.original, {
                [col.id]: newDate,
              } as Partial<T>);
            }
          }}
          required={col.required}
        />
      );
    },
    meta: {
      editable: true,
    },
    tooltipRender: (cell: CellContext<T, unknown>) => {
      const value = cell.getValue();
      if (!value) return t` `;
      try {
        return format(new Date(value as string), 'dd-MMM-yyyy');
      } catch {
        return String(value);
      }
    },
  };
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Date field type configuration
 */
export const dateFieldType: FieldTypeConfig = {
  placeholder: 'Select date...',
  ...M,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const dateReadOnly: ColumnRenderer = dateReadOnlyRenderer;

/** Editable renderer with proper typing */
export const dateEditable: ColumnRenderer = dateEditableRenderer;
